package dev.kuml.portal.web.api

import dev.kuml.portal.web.editor.DiagramOutlineResponse
import dev.kuml.portal.web.util.RetryAfter
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.dom.url.URL
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response
import org.w3c.fetch.SAME_ORIGIN
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

private val json = Json { ignoreUnknownKeys = true }
private val svgBlobPropertyBag = BlobPropertyBag(type = "image/svg+xml")

/** R-21: mirrors `dev.kuml.portal.server.share.SHARE_VIEW_TOKEN_HEADER` -- the header an unlocked
 * viewer sends its share view token on, never a query parameter (see that constant's server-side
 * KDoc: a query parameter would land in access logs / browser history / the `Referer` header). */
public const val SHARE_VIEW_TOKEN_HEADER: String = "X-Share-View-Token"

/**
 * Thin hand-written Browser-Fetch client for `kuml-portal-server`'s JSON REST API -- see
 * `build.gradle.kts`'s architecture-decision KDoc for why this exists instead of `kvision-rest`.
 *
 * Every request is sent with `credentials = "same-origin"`: this frontend's bundle and the API are
 * served from the same origin in both local dev (webpack-dev-server's `/api` proxy, see
 * `webpack.config.d/proxy.js`) and production (Caddy, see `deploy/Caddyfile`), so the existing
 * httpOnly session cookie is sent/received automatically -- no token storage, no CORS.
 *
 * @param onSessionExpired invoked exactly once per call that comes back `401` from an
 *   ALREADY-authenticated endpoint (projects/ai/account/logout/me) -- NOT from `/api/auth/login` or
 *   `/register`, where a `401`/`409` is a normal credential/conflict error, not a session expiry.
 *   The design spec's copy ("Ihre Sitzung ist abgelaufen. Bitte erneut anmelden.") and the redirect
 *   to `/login` are the caller's (the router's) responsibility; this class only signals the event.
 */
public class PortalApi(
    private val onSessionExpired: () -> Unit = {},
) {
    // ---------- Auth (no session-expiry interception: 401/409 here are normal auth outcomes) ----------

    public suspend fun register(
        email: String,
        password: String,
        displayName: String,
    ): ApiResult<UserResponse> =
        publicJsonRequest(
            "POST",
            "/api/auth/register",
            json.encodeToString(RegisterRequest(email, password, displayName, dev.kuml.portal.web.i18n.LanguageState.current.storageValue)),
        )

    public suspend fun login(
        email: String,
        password: String,
    ): ApiResult<UserResponse> = publicJsonRequest("POST", "/api/auth/login", json.encodeToString(LoginRequest(email, password)))

    public suspend fun logout() {
        rawRequest("POST", "/api/auth/logout")
    }

    public suspend fun me(): ApiResult<UserResponse> = authenticatedJsonRequest("GET", "/api/auth/me")

    // ---------- Email infrastructure wave: R-3 (verification) / R-4 (password reset) ----------

    public suspend fun verifyEmail(token: String): ApiResult<Unit> =
        publicJsonRequest("POST", "/api/auth/verify-email", json.encodeToString(VerifyEmailRequest(token)))

    public suspend fun resendVerification(): ApiResult<Unit> =
        authenticatedJsonRequest(
            "POST",
            "/api/auth/verification/resend",
            json.encodeToString(ResendVerificationRequest(dev.kuml.portal.web.i18n.LanguageState.current.storageValue)),
        )

    public suspend fun requestPasswordReset(email: String): ApiResult<Unit> =
        publicJsonRequest(
            "POST",
            "/api/auth/password-reset/request",
            json.encodeToString(PasswordResetRequest(email, dev.kuml.portal.web.i18n.LanguageState.current.storageValue)),
        )

    public suspend fun confirmPasswordReset(
        token: String,
        password: String,
    ): ApiResult<Unit> =
        publicJsonRequest(
            "POST",
            "/api/auth/password-reset/confirm",
            json.encodeToString(PasswordResetConfirmRequest(token, password)),
        )

    // ---------- Avatar (R-16, session-expiry interception applies) ----------

    /** `PUT /api/account/avatar` -- [image] is caller-encoded (see `ProfileScreen`'s
     * `encodeAvatar`); this method sends it verbatim as the raw request body, with `image.type`
     * (if set) as the `Content-Type` header. The server NEVER trusts that header -- it derives the
     * actual content type from the bytes themselves (see
     * `dev.kuml.portal.server.users.AvatarValidator`'s KDoc) -- so an empty/wrong [Blob.type] here
     * is a cosmetic-only concern, not a security one. */
    public suspend fun uploadAvatar(image: Blob): ApiResult<Unit> {
        val response = rawBinaryRequest("PUT", "/api/account/avatar", image) ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 204) {
            ApiResult.Success(Unit)
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    public suspend fun deleteAvatar(): ApiResult<Unit> {
        val response = rawRequest("DELETE", "/api/account/avatar") ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 204) {
            ApiResult.Success(Unit)
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    // ---------- Projects (session-expiry interception applies) ----------

    public suspend fun listProjects(): ApiResult<List<ProjectResponse>> = authenticatedJsonRequest("GET", "/api/projects")

    public suspend fun getProject(id: String): ApiResult<ProjectResponse> = authenticatedJsonRequest("GET", "/api/projects/$id")

    /** R-22: the server seeds an initial diagram inside the same transaction -- the response
     * carries both, so the caller can navigate straight to the new diagram without a second
     * roundtrip. */
    public suspend fun createProject(name: String): ApiResult<CreateProjectResponse> =
        authenticatedJsonRequest("POST", "/api/projects", json.encodeToString(CreateProjectRequest(name)))

    public suspend fun renameProject(
        id: String,
        name: String,
    ): ApiResult<ProjectResponse> =
        authenticatedJsonRequest("PATCH", "/api/projects/$id", json.encodeToString(UpdateProjectRequest(name = name)))

    public suspend fun deleteProject(id: String): ApiResult<Unit> {
        val response = rawRequest("DELETE", "/api/projects/$id") ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 204) {
            ApiResult.Success(Unit)
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    // ---------- Diagrams (R-22, session-expiry interception applies) ----------

    public suspend fun listDiagrams(projectId: String): ApiResult<List<DiagramResponse>> =
        authenticatedJsonRequest("GET", "/api/projects/$projectId/diagrams")

    /** [name] `null` lets the server assign the next free default name ("Diagramm N") -- so the
     * editor's `+`-tab can never fail with a 409 name collision. */
    public suspend fun createDiagram(
        projectId: String,
        name: String? = null,
    ): ApiResult<DiagramResponse> =
        authenticatedJsonRequest("POST", "/api/projects/$projectId/diagrams", json.encodeToString(CreateDiagramRequest(name)))

    public suspend fun renameDiagram(
        diagramId: String,
        name: String,
    ): ApiResult<DiagramResponse> =
        authenticatedJsonRequest("PATCH", "/api/diagrams/$diagramId", json.encodeToString(UpdateDiagramRequest(name)))

    public suspend fun deleteDiagram(diagramId: String): ApiResult<Unit> {
        val response = rawRequest("DELETE", "/api/diagrams/$diagramId") ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 204) {
            ApiResult.Success(Unit)
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    // ---------- Share links: owner side (R-21, session-expiry interception applies) ----------

    public suspend fun createShareLink(
        diagramId: String,
        password: String?,
        expiresAt: String?,
    ): ApiResult<IssuedShareLinkResponse> =
        authenticatedJsonRequest(
            "POST",
            "/api/diagrams/$diagramId/share-links",
            json.encodeToString(CreateShareLinkRequest.serializer(), CreateShareLinkRequest(password, expiresAt)),
        )

    public suspend fun listShareLinks(diagramId: String): ApiResult<List<ShareLinkResponse>> =
        authenticatedJsonRequest("GET", "/api/diagrams/$diagramId/share-links")

    public suspend fun revokeShareLink(shareLinkId: String): ApiResult<Unit> {
        val response = rawRequest("DELETE", "/api/share-links/$shareLinkId") ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 204) {
            ApiResult.Success(Unit)
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    // ---------- Share links: PUBLIC side (R-21, NO session-expiry interception -- a 401 here means
    // "this link needs a password", never "your session expired") ----------

    public suspend fun publicShare(token: String): ApiResult<PublicShareResponse> = publicJsonRequest("GET", "/api/share/$token")

    public suspend fun unlockShare(
        token: String,
        password: String,
    ): ApiResult<UnlockShareResponse> =
        publicJsonRequest(
            "POST",
            "/api/share/$token/unlock",
            json.encodeToString(UnlockShareRequest.serializer(), UnlockShareRequest(password)),
        )

    /** `GET /api/share/{token}/svg`, [viewToken] `null` for an unprotected link (or before
     * unlocking a protected one -- the server then answers [ShareSvgResult.PasswordRequired]). */
    public suspend fun publicShareSvg(
        token: String,
        viewToken: String?,
    ): ShareSvgResult {
        val extraHeaders = viewToken?.let { mapOf(SHARE_VIEW_TOKEN_HEADER to it) } ?: emptyMap()
        val response =
            rawRequest("GET", "/api/share/$token/svg", extraHeaders = extraHeaders) ?: return ShareSvgResult.NetworkError
        val status = response.status.toInt()
        return when (status) {
            200 -> ShareSvgResult.Rendered(toSvgBlobUrl(response.text().await()))
            204 -> ShareSvgResult.Empty
            401 -> ShareSvgResult.PasswordRequired
            404 -> ShareSvgResult.NotFound
            422 -> ShareSvgResult.ValidationFailed(decodeErrorBodyOrNull(response)?.message)
            429 ->
                ShareSvgResult.HttpError(
                    status,
                    decodeErrorBodyOrNull(response),
                    RetryAfter.parseSeconds(response.headers.get("Retry-After")),
                )
            else -> ShareSvgResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    // ---------- R-5: Social Login ----------

    /** `GET /api/auth/oauth/providers` -- the configured providers, so the login/register form can
     * hide a button whose `/start` would just 503 (e.g. local dev without secrets configured). */
    public suspend fun oauthProviders(): ApiResult<OAuthProvidersResponse> = publicJsonRequest("GET", "/api/auth/oauth/providers")

    /** `GET /api/account/oauth` -- the caller's own linked social-login connections. */
    public suspend fun oauthConnections(): ApiResult<List<OAuthConnectionResponse>> = authenticatedJsonRequest("GET", "/api/account/oauth")

    public suspend fun unlinkOAuth(provider: String): ApiResult<Unit> {
        val response = rawRequest("DELETE", "/api/account/oauth/$provider") ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 204) {
            ApiResult.Success(Unit)
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    public suspend fun usage(): ApiResult<UsageResponse> = authenticatedJsonRequest("GET", "/api/account/usage")

    /** R-15: the caller's own AI-generation activity calendar. */
    public suspend fun activity(): ApiResult<ActivityResponse> = authenticatedJsonRequest("GET", "/api/account/activity")

    // ---------- Consent (R-11) ----------

    /** `PATCH /api/account/consent` -- either argument left `null` leaves that field unchanged
     * server-side (see [UpdateConsentRequest]'s KDoc). */
    public suspend fun updateConsent(
        trainingOptOut: Boolean? = null,
        showcaseOptIn: Boolean? = null,
    ): ApiResult<UserResponse> =
        authenticatedJsonRequest(
            "PATCH",
            "/api/account/consent",
            json.encodeToString(UpdateConsentRequest.serializer(), UpdateConsentRequest(trainingOptOut, showcaseOptIn)),
        )

    // ---------- Profil-Stammdaten (R-23) ----------

    /** `PATCH /api/account/profile` -- returns the full [UserResponse], exactly like
     * [updateConsent], so the profile screen stays consistent without a second `GET /api/auth/me`. */
    public suspend fun updateProfile(displayName: String): ApiResult<UserResponse> =
        authenticatedJsonRequest(
            "PATCH",
            "/api/account/profile",
            json.encodeToString(UpdateProfileRequest.serializer(), UpdateProfileRequest(displayName)),
        )

    // ---------- Generation history / beanstandung (R-9/R-10) ----------

    public suspend fun listGenerations(
        diagramId: String,
        limit: Int = 1,
    ): ApiResult<List<GenerationSummaryResponse>> = authenticatedJsonRequest("GET", "/api/diagrams/$diagramId/generations?limit=$limit")

    public suspend fun reportGeneration(
        generationId: String,
        text: String,
    ): ApiResult<GenerationSummaryResponse> =
        authenticatedJsonRequest(
            "POST",
            "/api/generations/$generationId/report",
            json.encodeToString(ReportGenerationRequest.serializer(), ReportGenerationRequest(text)),
        )

    public suspend fun listGenerationComments(generationId: String): ApiResult<List<GenerationCommentResponse>> =
        authenticatedJsonRequest("GET", "/api/generations/$generationId/comments")

    public suspend fun addGenerationComment(
        generationId: String,
        body: String,
    ): ApiResult<GenerationCommentResponse> =
        authenticatedJsonRequest(
            "POST",
            "/api/generations/$generationId/comments",
            json.encodeToString(CreateGenerationCommentRequest.serializer(), CreateGenerationCommentRequest(body)),
        )

    // ---------- Diagram render / AI generation ----------

    public suspend fun renderProject(diagramId: String): RenderResult {
        val response = rawRequest("GET", "/api/diagrams/$diagramId/render") ?: return RenderResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return when (status) {
            200 -> {
                val svg = response.text().await()
                RenderResult.Rendered(svgBlobUrl = toSvgBlobUrl(svg), svg = svg)
            }
            204 -> RenderResult.Empty
            422 -> RenderResult.ValidationFailed(decodeErrorBodyOrNull(response)?.message)
            else -> RenderResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    /** R-24: the diagram's stored DSL source -- the data source the editor's "DSL anzeigen" panel
     * needs to show anything after a reload/tab switch ([renderProject] only ever returns SVG
     * bytes, never the DSL text behind them). */
    public suspend fun diagramDsl(diagramId: String): DslResult {
        val response = rawRequest("GET", "/api/diagrams/$diagramId/dsl") ?: return DslResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return when (status) {
            200 -> DslResult.Loaded(json.decodeFromString<DiagramDslResponse>(response.text().await()).dsl)
            204 -> DslResult.Empty
            else -> DslResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    /** R-27: `GET /api/diagrams/{id}/outline` -- the JSON projection of the SAME live-or-persisted
     * model [renderProject]/[diagramDsl] read from, with exactly the ids that also appear in the
     * rendered SVG's `data-kuml-id` attributes (see `editor/SvgInliner.kt`). Powers the inspector
     * panel's [InspectorModel.contentFor] selection lookups -- see `DiagramRoutes.kt`'s own KDoc
     * for why this exists instead of a second, client-side DSL-text parser. A plain
     * [authenticatedJsonRequest] (unlike [renderProject]/[diagramDsl]'s hand-rolled status
     * `when`): every status this route can return (200, 401, 404, 422, network error) already maps
     * onto [ApiResult] exactly, with no extra 204/empty-body case to special-case.
     */
    public suspend fun diagramOutline(diagramId: String): ApiResult<DiagramOutlineResponse> =
        authenticatedJsonRequest("GET", "/api/diagrams/$diagramId/outline")

    /** `POST /api/ai/context?format=...` (R-8) -- [content] is caller-selected raw file bytes,
     * [format] one of `AiContextFile.formatForFileName`'s closed set of wire values. Sent as the
     * raw request body via [rawBinaryRequest], exactly like [uploadAvatar] -- never materialized as
     * a JSON string first (would defeat the point of a binary body for a potentially 64 KiB file). */
    public suspend fun uploadAiContext(
        content: Blob,
        format: String,
    ): ApiResult<AiContextUploadResponse> {
        val response =
            rawBinaryRequest("POST", "/api/ai/context?format=$format", content) ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 200) {
            ApiResult.Success(json.decodeFromString<AiContextUploadResponse>(response.text().await()))
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    /** `DELETE /api/ai/context/{id}` -- always idempotent server-side (204 whether or not [contextId]
     * still existed), so this deliberately does not distinguish outcomes beyond network-vs-not. */
    public suspend fun deleteAiContext(contextId: String): ApiResult<Unit> {
        val response = rawRequest("DELETE", "/api/ai/context/$contextId") ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 204) {
            ApiResult.Success(Unit)
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    public suspend fun generateDiagram(
        diagramId: String,
        prompt: String,
        contextId: String? = null,
    ): GenerateResult {
        val response =
            rawRequest(
                "POST",
                "/api/diagrams/$diagramId/ai/generate",
                json.encodeToString(GenerateDiagramRequest(prompt, contextId)),
            ) ?: return GenerateResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return when (status) {
            200 -> {
                val body = json.decodeFromString<GenerateDiagramResponse>(response.text().await())
                GenerateResult.Success(
                    svgBlobUrl = toSvgBlobUrl(body.svg),
                    dsl = body.dsl,
                    aiUsage = parseAiUsageHeader(response.headers.get("X-AI-Monthly-Usage")),
                    generationId = body.generationId,
                )
            }
            429 ->
                GenerateResult.HttpError(
                    status,
                    decodeErrorBodyOrNull(response),
                    RetryAfter.parseSeconds(response.headers.get("Retry-After")),
                )
            else -> GenerateResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    // ---------- R-26: Persistent project-file store ----------

    /** `GET /api/projects/{id}/files`. */
    public suspend fun listProjectFiles(projectId: String): ApiResult<List<ProjectFileResponse>> =
        authenticatedJsonRequest("GET", "/api/projects/$projectId/files")

    /** `POST /api/projects/{id}/files?fileName=...&description=...` -- [content] is caller-selected
     * raw file bytes, sent as the raw request body via [rawBinaryRequest] (never re-encoded as
     * JSON first), exactly like [uploadAvatar]/[uploadAiContext]. [fileName]/[description] travel
     * as query parameters since the body is the file's raw bytes, not JSON -- both are
     * percent-encoded here so a name/description containing `&`/`?`/space cannot corrupt the query
     * string. */
    public suspend fun uploadProjectFile(
        projectId: String,
        content: Blob,
        fileName: String,
        description: String?,
    ): ApiResult<ProjectFileResponse> {
        val query =
            buildString {
                append("fileName=").append(encodeUriComponentForQuery(fileName))
                if (!description.isNullOrBlank()) {
                    append("&description=").append(encodeUriComponentForQuery(description))
                }
            }
        val response =
            rawBinaryRequest("POST", "/api/projects/$projectId/files?$query", content) ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 201) {
            ApiResult.Success(json.decodeFromString<ProjectFileResponse>(response.text().await()))
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    /** `PATCH /api/project-files/{id}`. */
    public suspend fun updateProjectFileDescription(
        fileId: String,
        description: String?,
    ): ApiResult<ProjectFileResponse> =
        authenticatedJsonRequest("PATCH", "/api/project-files/$fileId", json.encodeToString(UpdateProjectFileRequest(description)))

    /** `DELETE /api/project-files/{id}` -- always idempotent server-side, mirrors [deleteAiContext]. */
    public suspend fun deleteProjectFile(fileId: String): ApiResult<Unit> {
        val response = rawRequest("DELETE", "/api/project-files/$fileId") ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        interceptSessionExpiry(status)
        return if (status == 204) {
            ApiResult.Success(Unit)
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    /** `GET /api/projects/{id}/files/index.md` -- `null` on 204 (project has no files) as well as
     * on any network/HTTP error, since the panel's "view INDEX.md" link only needs a best-effort
     * preview. */
    public suspend fun projectFileIndexMarkdown(projectId: String): String? {
        val response = rawRequest("GET", "/api/projects/$projectId/files/index.md") ?: return null
        interceptSessionExpiry(response.status.toInt())
        return if (response.status.toInt() == 200) response.text().await() else null
    }

    /** Same-origin relative URL for `GET /api/project-files/{id}` -- suitable directly as an
     * `<img src>` (the browser sends the session cookie automatically for a same-origin request)
     * or as a download link target. */
    public fun projectFileUrl(fileId: String): String = "/api/project-files/$fileId"

    // ---------- Internals ----------

    private suspend inline fun <reified T> authenticatedJsonRequest(
        method: String,
        path: String,
        jsonBody: String? = null,
    ): ApiResult<T> {
        val result = publicJsonRequest<T>(method, path, jsonBody)
        if (result is ApiResult.HttpError && result.status == 401) {
            onSessionExpired()
        }
        return result
    }

    /**
     * @param jsonBody an ALREADY-encoded JSON body, produced by the caller via
     *   `json.encodeToString(ConcreteRequestType(...))` at its own call site -- deliberately not
     *   accepted here as a generic `Any?` and re-encoded internally, because kotlinx.serialization's
     *   reified `encodeToString<T>` resolves its serializer from the STATIC type parameter; routing
     *   an arbitrary request object through an `Any?`-typed parameter would silently try (and fail)
     *   to serialize it as `Any` instead of its real type.
     */
    private suspend inline fun <reified T> publicJsonRequest(
        method: String,
        path: String,
        jsonBody: String? = null,
    ): ApiResult<T> {
        val response = rawRequest(method, path, jsonBody) ?: return ApiResult.NetworkError
        val status = response.status.toInt()
        return if (status in 200..299) {
            // 204 No Content (the pre-existing case) AND 202 Accepted (new in the email-
            // infrastructure wave -- POST /verification/resend and /password-reset/request both
            // answer 202 with an empty body) carry no JSON to decode.
            if (status == 204 || status == 202) {
                @Suppress("UNCHECKED_CAST")
                ApiResult.Success(Unit as T)
            } else {
                ApiResult.Success(json.decodeFromString<T>(response.text().await()))
            }
        } else if (status == 429) {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response), RetryAfter.parseSeconds(response.headers.get("Retry-After")))
        } else {
            ApiResult.HttpError(status, decodeErrorBodyOrNull(response))
        }
    }

    private fun interceptSessionExpiry(status: Int) {
        if (status == 401) onSessionExpired()
    }

    private suspend fun decodeErrorBodyOrNull(response: Response): ErrorResponse? =
        try {
            json.decodeFromString<ErrorResponse>(response.text().await())
        } catch (e: Throwable) {
            null
        }

    private fun toSvgBlobUrl(svg: String): String {
        val blob = Blob(arrayOf(svg), svgBlobPropertyBag)
        return URL.createObjectURL(blob)
    }

    private fun parseAiUsageHeader(headerValue: String?): AiUsageHeader? {
        if (headerValue == null) return null
        val parts = headerValue.split("/")
        if (parts.size != 2) return null
        val used = parts[0].toIntOrNull() ?: return null
        val limit = parts[1].toIntOrNull() ?: return null
        return AiUsageHeader(used, limit)
    }

    /** Thin wrapper around the global JS `encodeURIComponent` -- same `js(...)` pattern as
     * [AvatarUrl]'s private helper (no typed Kotlin/JS stdlib binding exists for it). */
    private fun encodeUriComponentForQuery(value: String): String = js("encodeURIComponent(value)") as String

    private suspend fun rawRequest(
        method: String,
        path: String,
        jsonBody: String? = null,
        extraHeaders: Map<String, String> = emptyMap(),
    ): Response? =
        try {
            val headers = Headers()
            if (jsonBody != null) headers.append("Content-Type", "application/json")
            extraHeaders.forEach { (name, value) -> headers.append(name, value) }
            val init =
                RequestInit(
                    method = method,
                    headers = headers,
                    body = jsonBody,
                    credentials = RequestCredentials.SAME_ORIGIN,
                )
            window.fetch(path, init).await()
        } catch (e: Throwable) {
            null
        }

    /** [rawRequest]'s binary sibling -- `RequestInit.body` only accepts a [String] in that
     * function's signature, so a [Blob] body (the avatar upload path) needs its own request
     * builder rather than reusing [rawRequest]. */
    private suspend fun rawBinaryRequest(
        method: String,
        path: String,
        body: Blob,
    ): Response? =
        try {
            val headers = Headers()
            headers.append("Content-Type", body.type.ifEmpty { "application/octet-stream" })
            val init =
                RequestInit(
                    method = method,
                    headers = headers,
                    body = body,
                    credentials = RequestCredentials.SAME_ORIGIN,
                )
            window.fetch(path, init).await()
        } catch (e: Throwable) {
            null
        }
}

/** Revokes a previously created blob URL (see [PortalApi.renderProject] / [PortalApi.generateDiagram])
 * -- must be called before a `<img>` is pointed at a new one, or the old blob leaks for the
 * lifetime of the page (see the design plan's Blob-URL-lifecycle gotcha). No-op for `null`. */
public fun revokeBlobUrlIfAny(blobUrl: String?) {
    if (blobUrl != null) URL.revokeObjectURL(blobUrl)
}
